import {
	AuthorizationChannel,
	isUnsupportedCardStamp,
	NormalizedCardStamp,
	normalizeCardStamp,
	resolveAuthorizationChannel,
} from '../constants/authorizationChannels';
import { MERCHANT_NUMBER_RULES as RULES, MerchantNumberRule, MerchantNumberValue } from '../constants/merchantNumbers';

const parseValidityDate = (validUntil: string): Date | null => {
	const match = validUntil.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
	if (!match) return null;
	const [, day, month, year] = match;
	return new Date(Number(year), Number(month) - 1, Number(day), 23, 59, 59);
};

export const isMerchantRuleExpired = (validUntil: string, referenceDate = new Date()): boolean => {
	const validity = parseValidityDate(validUntil);
	if (!validity) return false;
	return referenceDate > validity;
};

export const installmentsMatch = (installmentsLabel: string, fee: number): boolean => {
	// Las reglas de la base arman las cuotas de su lista: "1 a 6, 10". Cada parte se lee como una de la tabla fija.
	const parts = installmentsLabel.split(',');
	if (parts.length > 1) return parts.some((part) => installmentsMatch(part, fee));

	const label = installmentsLabel.trim().toLowerCase();

	if (label === '—' || label === '-') return false;

	const singleMatch = label.match(/^(\d+)$/);
	if (singleMatch) return fee === Number(singleMatch[1]);

	const rangeMatch = label.match(/^(\d+)\s*a\s*(\d+)$/);
	if (rangeMatch) {
		const min = Number(rangeMatch[1]);
		const max = Number(rangeMatch[2]);
		return fee >= min && fee <= max;
	}

	const hastaMatch = label.match(/^hasta\s*(\d+)$/);
	if (hastaMatch) return fee <= Number(hastaMatch[1]);

	return false;
};

// Las reglas (`rules`) son las de la base que lee el modal (`useMerchantRules`) o, si la Support API no las da, la
// tabla fija. Sin pasarlas, la tabla fija: así siguen los llamadores que no tienen acceso a la base. Una regla de la
// base trae su canal (`channel`) y se lee como la lee la API (`admits` de get-vouchers-api); la tabla fija, como siempre.

const isBaseRule = (rule: MerchantNumberRule): boolean => rule.channel !== undefined;

/**
 * El sello de la tarjeta como lo lee la API para las reglas de la base (`normalizeCardBrand`): fuera de Visa, Master,
 * Amex y Oca, todo es "otro sello" (también Cabal y Diners). Una regla de otro sello vale solo para esos.
 */
const baseBrandOf = (cardStamp: string): NormalizedCardStamp => {
	const stamp = normalizeCardStamp(cardStamp);
	return ['visa', 'mastercard', 'amex', 'oca'].includes(stamp) ? stamp : 'other';
};

/** Hasta cuándo rige, para comparar; sin vencimiento (o sin una fecha que se entienda), nunca vence. */
const validityTime = (rule: MerchantNumberRule): number => parseValidityDate(rule.validUntil)?.getTime() ?? Infinity;

export const lookupMerchantNumbers = (
	carrierCode: string,
	cardStamp: string,
	fee: number,
	rules: readonly MerchantNumberRule[] = RULES,
): MerchantNumberRule[] => {
	const normalizedStamp = normalizeCardStamp(cardStamp);
	const baseBrand = baseBrandOf(cardStamp);
	const code = carrierCode.toUpperCase();

	return rules.filter((rule) => {
		if (rule.carrierCode !== code) return false;
		if (isBaseRule(rule)) {
			if (!rule.cardStamps.includes(baseBrand)) return false;
		} else if (!rule.cardStamps.includes(normalizedStamp) && !rule.cardStamps.includes('other')) return false;
		return installmentsMatch(rule.installmentsLabel, fee);
	});
};

export const lookupMerchantNumbersForCarrierAndCard = (
	carrierCode: string,
	cardStamp: string,
	rules: readonly MerchantNumberRule[] = RULES,
): MerchantNumberRule[] => {
	const normalizedStamp = normalizeCardStamp(cardStamp);
	const baseBrand = baseBrandOf(cardStamp);
	const code = carrierCode.toUpperCase();

	return rules.filter(
		(rule) =>
			rule.carrierCode === code &&
			(isBaseRule(rule)
				? rule.cardStamps.includes(baseBrand)
				: rule.cardStamps.includes(normalizedStamp) || (normalizedStamp === 'other' && rule.cardStamps.length > 0)),
	);
};

/**
 * La regla que se propone. Con las de la base, como `admits` de la API: un "No acepta tarjetas" del sello gana siempre;
 * si no, de las que admiten esas cuotas, la de vigencia más larga (sin vencimiento le gana a cualquier fecha; a igual
 * vigencia, la primera); si ninguna las admite, ninguna (`null`). Con la tabla fija, la primera que admite las cuotas
 * y, si ninguna, la primera del sello.
 */
export const getRecommendedMerchantRule = (
	carrierCode: string,
	cardStamp: string,
	fee: number,
	rules: readonly MerchantNumberRule[] = RULES,
): MerchantNumberRule | null => {
	const matchingFee = lookupMerchantNumbers(carrierCode, cardStamp, fee, rules);
	const byCard = lookupMerchantNumbersForCarrierAndCard(carrierCode, cardStamp, rules);

	if (byCard.some(isBaseRule)) {
		const refusal = byCard.find((rule) => rule.merchantNumber === 'NOT_ACCEPTED');
		if (refusal) return refusal;
		if (matchingFee.length === 0) return null;
		return matchingFee.reduce((best, rule) => (validityTime(rule) > validityTime(best) ? rule : best));
	}

	if (matchingFee.length > 0) return matchingFee[0];
	return byCard.length > 0 ? byCard[0] : null;
};

/**
 * ¿Hay reglas del sello pero ninguna admite esas cuotas? Solo pasa con las de la base: la tabla fija propone igual la
 * primera del sello. Sin ninguna regla del sello, no (eso es "No hay número de comercio configurado").
 */
export const noRuleAdmitsInstallments = (
	carrierCode: string,
	cardStamp: string,
	fee: number,
	rules: readonly MerchantNumberRule[] = RULES,
): boolean =>
	!getRecommendedMerchantRule(carrierCode, cardStamp, fee, rules) &&
	lookupMerchantNumbersForCarrierAndCard(carrierCode, cardStamp, rules).length > 0;

/**
 * Por dónde se autoriza. Con una regla de la base que admite la tarjeta, el canal de esa regla (`channel`); si es "sin
 * definir", o con la tabla fija, o sin regla que la admita, el de `authorizationChannels`. Si ese tampoco se sabe: con
 * código de comercio, Fiserv. "Solo por GDS" va por GDS salvo con call center; "No acepta tarjetas", Cabal y Diners,
 * sin tarjetas.
 */
export const resolveEffectiveAuthorizationChannel = (
	carrierCode: string,
	cardStamp: string,
	fee: number,
	rules: readonly MerchantNumberRule[] = RULES,
): AuthorizationChannel => {
	if (isUnsupportedCardStamp(cardStamp)) {
		return 'no_cards';
	}

	const recommended = getRecommendedMerchantRule(carrierCode, cardStamp, fee, rules);

	if (recommended?.merchantNumber === 'NOT_ACCEPTED') {
		return 'no_cards';
	}

	// La recomendada de la base es del sello de la tarjeta y admite sus cuotas: si no hay, no se hereda el canal de otra.
	const ruleChannel = recommended?.channel;
	const channel =
		ruleChannel && ruleChannel !== 'unknown' ? ruleChannel : resolveAuthorizationChannel(carrierCode, cardStamp);

	if (recommended?.merchantNumber === 'GDS_ONLY' && channel !== 'call_center') {
		return 'gds';
	}

	if (channel === 'unknown' && recommended?.merchantNumber && recommended.merchantNumber !== 'GDS_ONLY') {
		return 'fiserv';
	}

	return channel;
};

export const formatMerchantNumberDisplay = (value: MerchantNumberValue): string => {
	if (value === 'GDS_ONLY') return 'Solo por GDS';
	if (value === 'NOT_ACCEPTED') return 'No acepta tarjetas';
	return value;
};

export { normalizeCardStamp, resolveAuthorizationChannel };
